import * as React from "react";
import { Form, Formik } from "formik";
import Stack from "@mui/material/Stack";
import { TextInputField } from "components/formikFields/TextInputField/TextInputField";
import { LoadingButton } from "components/LoadingButton/LoadingButton";
import {
  ApplicationsService,
  Client,
  ClientConfig,
  ClientsService,
  LanguagesService,
  LltsLanguage,
  MtBillingType,
  UserGroup,
  SystemSettings,
  SystemSettingsService,
  MachineTranslationSettingsOverrides
} from "gen/clients";
import { useMutation, useQuery } from "react-query";
import { SnackbarApiError } from "components/SnackbarApiError/SnackbarApiError";
import { PageLoading } from "components/PageLoading/PageLoading";
import { SelectOption } from "@mui/base";
import { SelectManyAutocompleteField } from "components/formikFields/SelectManyAutocompleteField/SelectManyAutocompleteField";
import { ApiErrorMessage } from "components/ApiErrorMessage/ApiErrorMessage";
import { ApplicationId } from "types/ApplicationId";
import { LabelValue } from "components/LabelValue/LabelValue";
import { SwitchField } from "components/formikFields/SwitchField/SwitchField";
import { SelectOneField } from "components/formikFields/SelectOneField/SelectOneField";
import {
  allMtBillingTypeOptions,
  mtBillingTypeOptionsForPreset,
  allMtFileTypeOptions,
  PriceByRating,
  DEFAULT_PRICE_BY_RATING
} from "pages/SystemSettingsPage/tabs/ApplcationsPage/utils/MtUtils";
import { FormFieldObserver } from "components/formikFields/FormFieldObserver/FormFieldObserver";
import { default as dayjs } from "dayjs";
import {
  DATE_TIME_PICKER_FORMAT,
  DateTimePickerField
} from "components/formikFields/DateTimePickerField/DateTimePickerField";
import { Box, Alert, Chip, Typography } from "@mui/material";
import { useUserSession } from "hooks/useUserSession";
import { useClientReadOnly } from "contexts/ClientLockContext";
import { InfoIcon } from "components/InfoIcon/InfoIcon";

interface Props {
  clientConfig: ClientConfig;
  onSaveSuccess: () => void;
}

enum FieldName {
  enableTextTranslation = "enableTextTranslation",
  enableFileTranslation = "enableFileTranslation",
  enableMediaTranslation = "enableMediaTranslation",
  enableHumanEscalation = "enableHumanEscalation",
  enableHumanEscalationPricing = "enableHumanEscalationPricing",
  humanEscalationPriceRating1 = "humanEscalationPriceRating1",
  humanEscalationPriceRating2 = "humanEscalationPriceRating2",
  humanEscalationPriceRating3 = "humanEscalationPriceRating3",
  humanEscalationPriceRating4 = "humanEscalationPriceRating4",
  humanEscalationPriceRating5 = "humanEscalationPriceRating5",
  enableAiLinguisticQualityReview = "enableAiLinguisticQualityReview",
  aiLinguisticQualityReviewSampleSize = "aiLinguisticQualityReviewSampleSize",
  aiLinguisticQualityReviewDocumentSamplePageCount = "aiLinguisticQualityReviewDocumentSamplePageCount",
  showAiLinguisticQualityScorePerFile = "showAiLinguisticQualityScorePerFile",
  enableAiPostEditing = "enableAiPostEditing",
  enableAiToolbox = "enableAiToolbox",
  showCreateTaskButton = "showCreateTaskButton",
  showPostEditAction = "showPostEditAction",
  showWatermarkAction = "showWatermarkAction",
  enableWatermarking = "enableWatermarking",
  watermarkText = "watermarkText",
  maxRequestsPerUserPerDay = "maxRequestsPerUserPerDay",
  maxCharactersPerRequest = "maxCharactersPerRequest",
  maxFileSizeMB = "maxFileSizeMB",
  maxFilesPerRequest = "maxFilesPerRequest",
  maxFileRequestsPerClientPerMonth = "maxFileRequestsPerClientPerMonth",
  supportedLanguages = "supportedLanguages",
  billingType = "billingType",
  trialExpirationDate = "trialExpirationDate",
  maxAudioFileSizeMB = "maxAudioFileSizeMB",
  maxVideoFileSizeMB = "maxVideoFileSizeMB",
  allowedFileTypes = "allowedFileTypes"
}

interface FormValues {
  [FieldName.enableTextTranslation]: boolean;
  [FieldName.enableFileTranslation]: boolean;
  [FieldName.enableMediaTranslation]: boolean;
  [FieldName.enableHumanEscalation]: boolean;
  [FieldName.enableHumanEscalationPricing]: boolean;
  [FieldName.humanEscalationPriceRating1]: string;
  [FieldName.humanEscalationPriceRating2]: string;
  [FieldName.humanEscalationPriceRating3]: string;
  [FieldName.humanEscalationPriceRating4]: string;
  [FieldName.humanEscalationPriceRating5]: string;
  [FieldName.enableAiLinguisticQualityReview]: boolean;
  [FieldName.aiLinguisticQualityReviewSampleSize]: number;
  [FieldName.aiLinguisticQualityReviewDocumentSamplePageCount]: number;
  [FieldName.showAiLinguisticQualityScorePerFile]: boolean;
  [FieldName.enableAiPostEditing]: boolean;
  [FieldName.enableAiToolbox]: boolean;
  [FieldName.showCreateTaskButton]: boolean;
  [FieldName.showPostEditAction]: boolean;
  [FieldName.showWatermarkAction]: boolean;
  [FieldName.enableWatermarking]: boolean;
  [FieldName.watermarkText]: string;
  [FieldName.maxRequestsPerUserPerDay]: number;
  [FieldName.maxCharactersPerRequest]: number;
  [FieldName.maxFileSizeMB]: number;
  [FieldName.maxFilesPerRequest]: number;
  [FieldName.maxFileRequestsPerClientPerMonth]: string;
  [FieldName.supportedLanguages]: SelectOption<string>[];
  [FieldName.billingType]: string;
  [FieldName.trialExpirationDate]?: string;
  [FieldName.maxAudioFileSizeMB]: number;
  [FieldName.maxVideoFileSizeMB]: number;
  [FieldName.allowedFileTypes]: SelectOption<string>[];
}

const MachineTranslationSettings: React.FC<Props> = ({ clientConfig, onSaveSuccess }) => {
  const { roles } = useUserSession();
  const isReadOnly = useClientReadOnly();
  const [isFreeTrial, setIsFreeTrial] = React.useState(false);

  const {
    data: mtApp,
    isLoading: isMtAppLoading,
    error: mtAppError
  } = useQuery(["getMtApp"], {
    enabled: !clientConfig.machineTranslationSettings,
    queryFn: () => ApplicationsService.getApplication({ id: ApplicationId.machineTranslation })
  });

  // Fetch system settings to get app bundles and branches
  const { data: systemSettings } = useQuery<SystemSettings>(["getSystemSettings"], {
    queryFn: SystemSettingsService.getSystemSettings,
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    cacheTime: 0
  });

  // Fetch client data to get the branch
  const { data: client } = useQuery<Client>(["getClient", clientConfig.id], {
    queryFn: () => ClientsService.getClient({ clientId: clientConfig.id }),
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    cacheTime: 0
  });

  // Get the client's branch currency
  const clientBranchCurrency = React.useMemo(() => {
    if (!client?.branchId || !systemSettings?.lltsBranches) {
      return "USD"; // Default to USD if branch not found
    }
    const branch = systemSettings.lltsBranches.find(b => b.xtrfId === client.branchId);
    return branch?.currency || "USD";
  }, [client?.branchId, systemSettings?.lltsBranches]);

  const {
    data: lltsLanguages,
    isLoading: areLlltsLanguagesLoading,
    error: lltsLanguagesError
  } = useQuery(["getMtLltsLanguages"], {
    queryFn: () =>
      LanguagesService.listLltsLanguages({
        azureIdExists: true
      })
  });

  const lltsLanguageById: Record<string, LltsLanguage> = React.useMemo(
    () =>
      (lltsLanguages || []).reduce(
        (prevValue, currentValue) => {
          // eslint-disable-next-line no-param-reassign
          prevValue[currentValue.id] = currentValue;
          return prevValue;
        },
        {} as Record<string, LltsLanguage>
      ),
    [lltsLanguages]
  );

  const languageOptions = React.useMemo(
    () =>
      (lltsLanguages?.map(l => ({ label: l.name, value: l.id })) || []).sort((o1, o2) =>
        o1.label.localeCompare(o2.label)
      ),
    [lltsLanguages]
  );

  // Get the assigned app bundle and MT preset
  const assignedAppBundle = React.useMemo(() => {
    if (!clientConfig.appBundleId || !systemSettings?.appBundles) {
      return null;
    }
    return systemSettings.appBundles.find(bundle => bundle.id === clientConfig.appBundleId);
  }, [clientConfig.appBundleId, systemSettings?.appBundles]);

  const mtPresetSettings = React.useMemo(() => {
    if (!assignedAppBundle) {
      return null;
    }

    // Find the Machine Translation application in the app bundle
    const mtApp = assignedAppBundle.applications?.find(app => app.applicationId === ApplicationId.machineTranslation);
    if (!mtApp?.applicationSettingsPresetId) {
      return null;
    }

    // Get the preset settings from the MT application
    // Note: We would need to fetch the full application details to get the preset settings
    // For now, we'll return the preset ID and handle the settings resolution in the form values
    return {
      presetId: mtApp.applicationSettingsPresetId,
      // The actual preset settings would be fetched separately or passed down
      settings: null // This would contain the actual MachineTranslationSettings from the preset
    };
  }, [assignedAppBundle]);

  // Fetch full MT application details to get preset settings
  const { data: fullMtApp } = useQuery(
    ["getMtAppFull"],
    () => ApplicationsService.getApplication({ id: ApplicationId.machineTranslation }),
    {
      enabled: !!mtPresetSettings?.presetId,
      retry: false,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      cacheTime: 0
    }
  );

  // Get the actual preset settings
  const actualPresetSettings = React.useMemo(() => {
    if (!mtPresetSettings?.presetId || !fullMtApp?.settingPresets) {
      return null;
    }

    const preset = fullMtApp.settingPresets.find(p => p.id === mtPresetSettings.presetId);
    return preset?.settings || null;
  }, [mtPresetSettings?.presetId, fullMtApp?.settingPresets]);

  // Determine if we're in app bundle mode (preset-based configuration)
  const isAppBundleMode = !!actualPresetSettings;

  // Helper function to check if a field has an override
  const hasOverride = React.useCallback(
    (field: string) => {
      if (!isAppBundleMode || !clientConfig.machineTranslationSettingsOverrides) {
        return false;
      }
      return (clientConfig.machineTranslationSettingsOverrides as any)[field] !== undefined;
    },
    [isAppBundleMode, clientConfig.machineTranslationSettingsOverrides]
  );

  const {
    mutateAsync,
    error: mutateError,
    isLoading: isMutating
  } = useMutation(ClientsService.updateClientConfig, { onSuccess: onSaveSuccess });

  const initialValues = React.useMemo(() => {
    // Value resolution logic based on app bundle mode
    const getFieldValue = (field: keyof any, isEditableField: boolean) => {
      if (isAppBundleMode && actualPresetSettings) {
        if (isEditableField) {
          // For editable fields: overrides -> preset -> default
          const overrideValue = (clientConfig.machineTranslationSettingsOverrides as any)?.[field];
          if (overrideValue !== undefined) {
            return overrideValue;
          }
          return (actualPresetSettings as any)[field];
        } else {
          // For disabled fields: always use preset values
          return (actualPresetSettings as any)[field];
        }
      } else {
        // Normal mode: use existing logic
        const machineTranslationSettings = clientConfig.machineTranslationSettings || mtApp?.settings;
        return (machineTranslationSettings as any)?.[field];
      }
    };

    // Helper to get supported languages with proper resolution
    const getSupportedLanguages = () => {
      let languageIds: string[] = [];

      if (isAppBundleMode && actualPresetSettings) {
        // For editable field: overrides -> preset -> default
        const overrideValue = (clientConfig.machineTranslationSettingsOverrides as any)?.supportedLltsLanguages;
        if (overrideValue !== undefined) {
          languageIds = overrideValue;
        } else {
          languageIds = actualPresetSettings.supportedLltsLanguages || [];
        }
      } else {
        // Normal mode
        const machineTranslationSettings = clientConfig.machineTranslationSettings || mtApp?.settings;
        languageIds = machineTranslationSettings?.supportedLltsLanguages || [];
      }

      return languageIds
        .filter(langId => lltsLanguageById[langId] && !lltsLanguageById[langId]?.isDisabled)
        .map(langId => ({
          label: lltsLanguageById[langId]?.name || langId,
          value: langId
        }))
        .sort((o1, o2) => o1.label.localeCompare(o2.label));
    };

    // Helper to get allowed file types with proper resolution
    const getAllowedFileTypes = () => {
      let fileTypes: string[] | undefined;

      if (isAppBundleMode && actualPresetSettings) {
        // In app bundle mode, always use preset values (no overrides for this field)
        fileTypes = actualPresetSettings.allowedFileTypes;
      } else {
        // Normal mode
        const machineTranslationSettings = clientConfig.machineTranslationSettings || mtApp?.settings;
        fileTypes = machineTranslationSettings?.allowedFileTypes;
      }

      // If allowedFileTypes is undefined, treat as all file types are allowed
      if (fileTypes === undefined) {
        return allMtFileTypeOptions;
      }

      // Otherwise, filter based on the specified file types
      return allMtFileTypeOptions.filter(option => fileTypes!.includes(option.value as any));
    };

    // Helper to get human escalation price by rating with proper resolution
    const getHumanEscalationPriceByRating = (): PriceByRating => {
      if (isAppBundleMode && actualPresetSettings) {
        // For editable field: overrides -> preset -> default
        const overrideValue = (clientConfig.machineTranslationSettingsOverrides as any)?.humanEscalationPriceByRating;
        if (overrideValue !== undefined) {
          return overrideValue;
        }
        return (actualPresetSettings as any).humanEscalationPriceByRating || DEFAULT_PRICE_BY_RATING;
      } else {
        // Normal mode
        const machineTranslationSettings = clientConfig.machineTranslationSettings || mtApp?.settings;
        return (machineTranslationSettings as any)?.humanEscalationPriceByRating || DEFAULT_PRICE_BY_RATING;
      }
    };

    const maxFileRequestsPerClientPerMonthValue = getFieldValue("maxFileRequestsPerClientPerMonth", false);
    const priceByRating = getHumanEscalationPriceByRating();
    const humanEscalationPricingValue = getFieldValue("enableHumanEscalationPricing", true);

    // Helper to format price with 2 decimal places
    const formatPrice = (price: number | undefined): string => {
      const numPrice = price || 0;
      return numPrice.toFixed(2);
    };

    return {
      [FieldName.billingType]: getFieldValue("billingType", true) || MtBillingType.FREE,
      [FieldName.trialExpirationDate]: getFieldValue("trialExpirationDate", true)
        ? dayjs(getFieldValue("trialExpirationDate", true)).format(DATE_TIME_PICKER_FORMAT)
        : "",
      [FieldName.enableTextTranslation]: !getFieldValue("disableTextTranslation", false),
      [FieldName.enableFileTranslation]: !getFieldValue("disableFileTranslation", false),
      [FieldName.enableMediaTranslation]: getFieldValue("disableMediaTranslation", false) === false,
      [FieldName.enableHumanEscalation]: !getFieldValue("disableHumanEscalation", true),
      [FieldName.enableHumanEscalationPricing]: humanEscalationPricingValue !== false,
      [FieldName.humanEscalationPriceRating1]: formatPrice(priceByRating["1"]),
      [FieldName.humanEscalationPriceRating2]: formatPrice(priceByRating["2"]),
      [FieldName.humanEscalationPriceRating3]: formatPrice(priceByRating["3"]),
      [FieldName.humanEscalationPriceRating4]: formatPrice(priceByRating["4"]),
      [FieldName.humanEscalationPriceRating5]: formatPrice(priceByRating["5"]),
      [FieldName.enableAiLinguisticQualityReview]: getFieldValue("enableAiLinguisticQualityReview", false) || false,
      [FieldName.aiLinguisticQualityReviewSampleSize]:
        getFieldValue("aiLinguisticQualityReviewSampleSize", false) || 1000,
      [FieldName.aiLinguisticQualityReviewDocumentSamplePageCount]:
        getFieldValue("aiLinguisticQualityReviewDocumentSamplePageCount", false) || 5,
      [FieldName.showAiLinguisticQualityScorePerFile]:
        getFieldValue("showAiLinguisticQualityScorePerFile", false) || false,
      [FieldName.enableAiPostEditing]: getFieldValue("enableAiPostEditing", false) || false,
      [FieldName.enableAiToolbox]: getFieldValue("enableAiToolbox", false) || false,
      [FieldName.showCreateTaskButton]: !getFieldValue("aiToolboxSettings", false)?.hideCreateTaskButton,
      [FieldName.showPostEditAction]: !getFieldValue("aiToolboxSettings", false)?.hidePostEditAction,
      [FieldName.showWatermarkAction]: !getFieldValue("aiToolboxSettings", false)?.hideWatermarkAction,
      [FieldName.enableWatermarking]: getFieldValue("enableWatermarking", true) || false,
      [FieldName.watermarkText]: getFieldValue("watermarkText", true) || "",
      [FieldName.maxRequestsPerUserPerDay]: getFieldValue("maxRequestsPerUserPerDay", false) || 0,
      [FieldName.maxCharactersPerRequest]: getFieldValue("maxCharactersPerRequest", false) || 0,
      [FieldName.maxFileSizeMB]: getFieldValue("maxFileSizeMB", false) || 0,
      [FieldName.maxAudioFileSizeMB]: getFieldValue("maxAudioFileSizeMB", false) || 0,
      [FieldName.maxVideoFileSizeMB]: getFieldValue("maxVideoFileSizeMB", false) || 0,
      [FieldName.maxFilesPerRequest]: getFieldValue("maxFilesPerRequest", false) || 0,
      [FieldName.maxFileRequestsPerClientPerMonth]: maxFileRequestsPerClientPerMonthValue
        ? `${maxFileRequestsPerClientPerMonthValue}`
        : "",
      [FieldName.supportedLanguages]: getSupportedLanguages(),
      [FieldName.allowedFileTypes]: getAllowedFileTypes()
    };
  }, [
    clientConfig.machineTranslationSettings,
    clientConfig.machineTranslationSettingsOverrides,
    lltsLanguageById,
    mtApp?.settings,
    isAppBundleMode,
    actualPresetSettings
  ]);

  const validate = React.useCallback((formValues: FormValues) => {
    const errors: Record<string, string> = {};
    if (
      !formValues[FieldName.enableTextTranslation] &&
      !formValues[FieldName.enableFileTranslation] &&
      !formValues[FieldName.enableMediaTranslation]
    ) {
      errors[FieldName.enableTextTranslation] = "At least one of Text, File, or Media translation must be enabled";
      errors[FieldName.enableFileTranslation] = "At least one of Text, File, or Media translation must be enabled";
      errors[FieldName.enableMediaTranslation] = "At least one of Text, File, or Media translation must be enabled";
    }

    if (formValues[FieldName.billingType] === MtBillingType.FREE_TRIAL) {
      const trialExpirationValue = dayjs(formValues[FieldName.trialExpirationDate], DATE_TIME_PICKER_FORMAT).toDate();
      if (!trialExpirationValue) {
        errors[FieldName.trialExpirationDate] = "Expiration time is required for Free Trial";
      } else if (trialExpirationValue <= new Date()) {
        errors[FieldName.trialExpirationDate] = "Expiration time cannot be in the past";
      }
    }

    // Validate AI Linguistic Quality Review sample size
    if (formValues[FieldName.enableAiLinguisticQualityReview]) {
      const sampleSize = formValues[FieldName.aiLinguisticQualityReviewSampleSize];
      if (!sampleSize || sampleSize <= 0) {
        errors[FieldName.aiLinguisticQualityReviewSampleSize] = "Sample size must be a positive number";
      } else if (sampleSize > 100000) {
        errors[FieldName.aiLinguisticQualityReviewSampleSize] = "Sample size cannot exceed 100,000 characters";
      } else if (!Number.isInteger(sampleSize)) {
        errors[FieldName.aiLinguisticQualityReviewSampleSize] = "Sample size must be a whole number";
      }

      // Validate AI Linguistic Quality Review document sample page count
      const pageCount = formValues[FieldName.aiLinguisticQualityReviewDocumentSamplePageCount];
      if (!pageCount || pageCount <= 0) {
        errors[FieldName.aiLinguisticQualityReviewDocumentSamplePageCount] = "Page count must be a positive number";
      } else if (!Number.isInteger(pageCount)) {
        errors[FieldName.aiLinguisticQualityReviewDocumentSamplePageCount] = "Page count must be a whole number";
      }
    }

    // Validate maximum file requests per client per month
    const maxFileRequests = formValues[FieldName.maxFileRequestsPerClientPerMonth];
    if (maxFileRequests && maxFileRequests !== "") {
      const numValue = parseInt(maxFileRequests, 10);
      if (isNaN(numValue) || numValue <= 0) {
        errors[FieldName.maxFileRequestsPerClientPerMonth] = "Must be a positive number";
      }
    }

    // Validate watermark text
    if (formValues[FieldName.enableWatermarking]) {
      const watermarkText = formValues[FieldName.watermarkText];
      if (!watermarkText || watermarkText.trim() === "") {
        errors[FieldName.watermarkText] = "Watermark text is required when watermarking is enabled";
      } else if (watermarkText.length > 30) {
        errors[FieldName.watermarkText] = "Watermark text cannot exceed 30 characters";
      }
    }

    // Validate human escalation prices
    if (formValues[FieldName.enableHumanEscalation] && formValues[FieldName.enableHumanEscalationPricing]) {
      const priceFields = [
        FieldName.humanEscalationPriceRating1,
        FieldName.humanEscalationPriceRating2,
        FieldName.humanEscalationPriceRating3,
        FieldName.humanEscalationPriceRating4,
        FieldName.humanEscalationPriceRating5
      ];

      priceFields.forEach(field => {
        const priceValue = formValues[field] as string;
        if (priceValue !== undefined && priceValue !== "") {
          const numValue = parseFloat(priceValue);
          if (isNaN(numValue)) {
            errors[field] = "Must be a valid number";
          } else if (numValue < 0) {
            errors[field] = "Price cannot be negative";
          } else if (!/^\d+(\.\d{1,2})?$/.test(priceValue)) {
            errors[field] = "Price can have maximum 2 decimal places";
          }
        }
      });
    }

    return errors;
  }, []);

  const onSubmit = React.useCallback(
    async (values: FormValues) => {
      const selectedLanguageOptions = values[FieldName.supportedLanguages];
      const selectedLanguageIds = selectedLanguageOptions.map(l => l.value);
      const selectedFileTypeOptions = values[FieldName.allowedFileTypes];
      const selectedFileTypes = selectedFileTypeOptions.map(option => option.value as any);

      // If all file types are selected, save as undefined (default behavior)
      const allowedFileTypesToSave =
        selectedFileTypes.length === allMtFileTypeOptions.length ? undefined : selectedFileTypes;

      const trialExpirationValue =
        values[FieldName.billingType] === MtBillingType.FREE_TRIAL
          ? dayjs(values[FieldName.trialExpirationDate], DATE_TIME_PICKER_FORMAT).toDate()
          : undefined;

      if (isAppBundleMode && actualPresetSettings) {
        // App bundle mode: only save overrides for editable fields
        const overrides: MachineTranslationSettingsOverrides = {};

        // Only save values that differ from preset settings
        if (values[FieldName.billingType] !== actualPresetSettings.billingType) {
          overrides.billingType = values[FieldName.billingType] as MtBillingType;
        }

        if (trialExpirationValue?.toISOString() !== actualPresetSettings.trialExpirationDate) {
          overrides.trialExpirationDate = trialExpirationValue?.toISOString();
        }

        if (!values[FieldName.enableHumanEscalation] !== actualPresetSettings.disableHumanEscalation) {
          overrides.disableHumanEscalation = !values[FieldName.enableHumanEscalation];
        }

        const presetEnableHumanEscalationPricing = actualPresetSettings.enableHumanEscalationPricing ?? true;
        if (values[FieldName.enableHumanEscalationPricing] !== presetEnableHumanEscalationPricing) {
          overrides.enableHumanEscalationPricing = values[FieldName.enableHumanEscalationPricing];
        }

        // Compare supported languages arrays
        const presetLanguages = actualPresetSettings.supportedLltsLanguages || [];
        const areLanguagesDifferent =
          selectedLanguageIds.length !== presetLanguages.length ||
          !selectedLanguageIds.every(id => presetLanguages.includes(id));

        if (areLanguagesDifferent) {
          overrides.supportedLltsLanguages = selectedLanguageIds;
        }

        // Watermarking fields are always editable
        if (values[FieldName.enableWatermarking] !== actualPresetSettings.enableWatermarking) {
          overrides.enableWatermarking = values[FieldName.enableWatermarking];
        }

        if (values[FieldName.watermarkText] !== (actualPresetSettings.watermarkText || "")) {
          overrides.watermarkText = values[FieldName.enableWatermarking] ? values[FieldName.watermarkText] : undefined;
        }

        // Compare human escalation price by rating
        const formPriceByRating: PriceByRating = {
          "1": parseFloat(values[FieldName.humanEscalationPriceRating1]) || 0,
          "2": parseFloat(values[FieldName.humanEscalationPriceRating2]) || 0,
          "3": parseFloat(values[FieldName.humanEscalationPriceRating3]) || 0,
          "4": parseFloat(values[FieldName.humanEscalationPriceRating4]) || 0,
          "5": parseFloat(values[FieldName.humanEscalationPriceRating5]) || 0
        };
        const presetPriceByRating =
          (actualPresetSettings as any).humanEscalationPriceByRating || DEFAULT_PRICE_BY_RATING;
        const isPriceByRatingDifferent =
          formPriceByRating["1"] !== presetPriceByRating["1"] ||
          formPriceByRating["2"] !== presetPriceByRating["2"] ||
          formPriceByRating["3"] !== presetPriceByRating["3"] ||
          formPriceByRating["4"] !== presetPriceByRating["4"] ||
          formPriceByRating["5"] !== presetPriceByRating["5"];

        if (isPriceByRatingDifferent) {
          overrides.humanEscalationPriceByRating = formPriceByRating;
        }

        await mutateAsync({
          id: clientConfig.id,
          requestBody: {
            ...clientConfig,
            machineTranslationSettingsOverrides: overrides
          }
        });
      } else {
        // Normal mode: save all settings as before
        await mutateAsync({
          id: clientConfig.id,
          requestBody: {
            ...clientConfig,
            machineTranslationSettings: {
              billingType: values[FieldName.billingType] as MtBillingType,
              trialExpirationDate: trialExpirationValue?.toISOString(),
              disableTextTranslation: !values[FieldName.enableTextTranslation],
              disableFileTranslation: !values[FieldName.enableFileTranslation],
              disableMediaTranslation: !values[FieldName.enableMediaTranslation],
              disableHumanEscalation: !values[FieldName.enableHumanEscalation],
              enableHumanEscalationPricing: values[FieldName.enableHumanEscalationPricing],
              humanEscalationPriceByRating: {
                "1": parseFloat(values[FieldName.humanEscalationPriceRating1]) || 0,
                "2": parseFloat(values[FieldName.humanEscalationPriceRating2]) || 0,
                "3": parseFloat(values[FieldName.humanEscalationPriceRating3]) || 0,
                "4": parseFloat(values[FieldName.humanEscalationPriceRating4]) || 0,
                "5": parseFloat(values[FieldName.humanEscalationPriceRating5]) || 0
              } as PriceByRating,
              enableAiLinguisticQualityReview: values[FieldName.enableAiLinguisticQualityReview],
              aiLinguisticQualityReviewSampleSize: values[FieldName.aiLinguisticQualityReviewSampleSize],
              aiLinguisticQualityReviewDocumentSamplePageCount:
                values[FieldName.aiLinguisticQualityReviewDocumentSamplePageCount],
              showAiLinguisticQualityScorePerFile: values[FieldName.showAiLinguisticQualityScorePerFile],
              enableAiPostEditing: values[FieldName.enableAiPostEditing],
              enableAiToolbox: values[FieldName.enableAiToolbox],
              aiToolboxSettings: {
                hideCreateTaskButton: !values[FieldName.showCreateTaskButton],
                hidePostEditAction: !values[FieldName.showPostEditAction],
                hideWatermarkAction: !values[FieldName.showWatermarkAction]
              },
              enableWatermarking: values[FieldName.enableWatermarking],
              watermarkText: values[FieldName.enableWatermarking] ? values[FieldName.watermarkText] : undefined,
              maxCharactersPerRequest: values[FieldName.maxCharactersPerRequest],
              maxRequestsPerUserPerDay: values[FieldName.maxRequestsPerUserPerDay],
              maxFileSizeMB: values[FieldName.maxFileSizeMB],
              maxFilesPerRequest: values[FieldName.maxFilesPerRequest],
              maxFileRequestsPerClientPerMonth: values[FieldName.maxFileRequestsPerClientPerMonth]
                ? parseInt(values[FieldName.maxFileRequestsPerClientPerMonth], 10)
                : undefined,
              supportedLltsLanguages: selectedLanguageIds,
              maxAudioFileSizeMB: values[FieldName.maxAudioFileSizeMB],
              maxVideoFileSizeMB: values[FieldName.maxVideoFileSizeMB],
              allowedFileTypes: allowedFileTypesToSave
            }
          }
        });
      }
    },
    [clientConfig, mutateAsync, isAppBundleMode, actualPresetSettings]
  );

  const onResetToDefaultClick = React.useCallback(async () => {
    if (isAppBundleMode) {
      // In app bundle mode, clear overrides
      await mutateAsync({
        id: clientConfig.id,
        requestBody: {
          ...clientConfig,
          machineTranslationSettingsOverrides: undefined
        }
      });
    } else {
      // Normal mode: clear machine translation settings
      await mutateAsync({
        id: clientConfig.id,
        requestBody: {
          ...clientConfig,
          machineTranslationSettings: undefined
        }
      });
    }
  }, [clientConfig, mutateAsync, isAppBundleMode]);

  const handleBillingTypeChange = React.useCallback((billingType: string) => {
    setIsFreeTrial(billingType === MtBillingType.FREE_TRIAL);
  }, []);

  const isLoading = isMtAppLoading || areLlltsLanguagesLoading;
  const error = mtAppError || lltsLanguagesError;

  if (isLoading) {
    return <PageLoading />;
  }

  if (error) {
    return <ApiErrorMessage apiError={error} />;
  }

  const isSuperUser = roles?.includes(UserGroup.SUPER_USER);

  return (
    <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate} enableReinitialize={true}>
      {({ isSubmitting, values }) => (
        <Form noValidate={true} autoComplete="off">
          {mutateError && <SnackbarApiError error={mutateError} />}
          <FormFieldObserver fieldName={FieldName.billingType} onChange={handleBillingTypeChange} />

          {/* Show informational alert when app bundle is assigned */}
          {isAppBundleMode && assignedAppBundle && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Machine Translation settings are configured in the app bundle '{assignedAppBundle.name}'.
            </Alert>
          )}

          <Stack spacing={3}>
            {/* General Settings */}
            <Box sx={{ border: 1, borderColor: "grey.300", borderRadius: 1, p: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                General Settings
              </Typography>
              <Stack spacing={3}>
                <LabelValue
                  label="Text Translation"
                  value={
                    <SwitchField
                      name={FieldName.enableTextTranslation}
                      label={values[FieldName.enableTextTranslation] ? "Enabled" : "Disabled"}
                      disabled={isAppBundleMode || isReadOnly} // Disabled in app bundle mode or when the client is locked
                    />
                  }
                />
                <LabelValue
                  label="File Translation"
                  value={
                    <SwitchField
                      name={FieldName.enableFileTranslation}
                      label={values[FieldName.enableFileTranslation] ? "Enabled" : "Disabled"}
                      disabled={isAppBundleMode || isReadOnly} // Disabled in app bundle mode or when the client is locked
                    />
                  }
                />
                <LabelValue
                  label="Multimedia Translation"
                  value={
                    <Stack direction="row" spacing={1} alignItems="center">
                      <SwitchField
                        name={FieldName.enableMediaTranslation}
                        label={values[FieldName.enableMediaTranslation] ? "Enabled" : "Disabled"}
                        disabled={!isSuperUser || isAppBundleMode || isReadOnly} // Disabled in app bundle mode, for non-super users, or when the client is locked
                      />
                      {!isSuperUser && (
                        <InfoIcon tooltip="Only super users can enable/disable multimedia translation." />
                      )}
                    </Stack>
                  }
                />
                <LabelValue
                  label="Human Escalation"
                  value={
                    <Stack direction="row" spacing={1} alignItems="center">
                      <SwitchField
                        name={FieldName.enableHumanEscalation}
                        label={values[FieldName.enableHumanEscalation] ? "Enabled" : "Disabled"}
                        // This field is editable in app bundle mode
                      />
                      {hasOverride("disableHumanEscalation") && (
                        <Chip label="Override" size="small" color="primary" variant="outlined" />
                      )}
                    </Stack>
                  }
                />
                <LabelValue
                  label="AI Linguistic Quality Review"
                  value={
                    <SwitchField
                      name={FieldName.enableAiLinguisticQualityReview}
                      label={values[FieldName.enableAiLinguisticQualityReview] ? "Enabled" : "Disabled"}
                      disabled={isAppBundleMode || isReadOnly} // Disabled in app bundle mode or when the client is locked
                    />
                  }
                />
                <LabelValue
                  label="AI Toolbox"
                  value={
                    <SwitchField
                      name={FieldName.enableAiToolbox}
                      label={values[FieldName.enableAiToolbox] ? "Enabled" : "Disabled"}
                      disabled={isAppBundleMode || isReadOnly} // Disabled in app bundle mode or when the client is locked
                    />
                  }
                />
                <Stack direction="row" spacing={1} alignItems="flex-end">
                  <Box sx={{ flexGrow: 1 }}>
                    <SelectOneField
                      name={FieldName.billingType}
                      label="MT Billing Type"
                      options={isAppBundleMode ? mtBillingTypeOptionsForPreset : allMtBillingTypeOptions}
                      required={true}
                      // This field is editable in app bundle mode
                    />
                  </Box>
                  {hasOverride("billingType") && (
                    <Chip label="Override" size="small" color="primary" variant="outlined" sx={{ mb: 0.5 }} />
                  )}
                </Stack>
                {isFreeTrial && (
                  <Stack direction="row" spacing={1} alignItems="flex-end">
                    <Box sx={{ flexGrow: 1 }}>
                      <DateTimePickerField
                        name={FieldName.trialExpirationDate}
                        label="Trial Expiration Time"
                        required={true}
                        disablePast={true}
                        // This field is editable in app bundle mode
                      />
                    </Box>
                    {hasOverride("trialExpirationDate") && (
                      <Chip label="Override" size="small" color="primary" variant="outlined" sx={{ mb: 0.5 }} />
                    )}
                  </Stack>
                )}
                <TextInputField
                  name={FieldName.maxRequestsPerUserPerDay}
                  label="Maximum number of requests per user per day"
                  type="number"
                  required={true}
                  disabled={isAppBundleMode || isReadOnly} // Disabled in app bundle mode or when the client is locked
                />
                <TextInputField
                  name={FieldName.maxFilesPerRequest}
                  label="Maximum number of files per request"
                  type="number"
                  required={true}
                  disabled={isAppBundleMode || isReadOnly} // Disabled in app bundle mode or when the client is locked
                />
                <TextInputField
                  name={FieldName.maxFileRequestsPerClientPerMonth}
                  label="Maximum file requests per month for client"
                  type="number"
                  required={false}
                  disabled={isAppBundleMode || isReadOnly} // Disabled in app bundle mode or when the client is locked
                  InputLabelProps={{ shrink: true }}
                />
                <Stack direction="row" spacing={1} alignItems="flex-end">
                  <Box sx={{ flexGrow: 1 }}>
                    <SelectManyAutocompleteField
                      name={FieldName.supportedLanguages}
                      label="Supported Languages"
                      options={languageOptions}
                      required={true}
                      // This field is editable in app bundle mode
                    />
                  </Box>
                  {hasOverride("supportedLltsLanguages") && (
                    <Chip label="Override" size="small" color="primary" variant="outlined" sx={{ mb: 0.5 }} />
                  )}
                </Stack>
              </Stack>
            </Box>

            {/* Text Translation Settings */}
            {values[FieldName.enableTextTranslation] && (
              <Box sx={{ border: 1, borderColor: "grey.300", borderRadius: 1, p: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                  Text Translation Settings
                </Typography>
                <Stack spacing={3}>
                  <TextInputField
                    name={FieldName.maxCharactersPerRequest}
                    label="Maximum characters per text translation request"
                    type="number"
                    required={true}
                    disabled={isAppBundleMode || isReadOnly} // Disabled in app bundle mode or when the client is locked
                  />
                  <LabelValue
                    label="AI Text Post Editing"
                    value={
                      <SwitchField
                        name={FieldName.enableAiPostEditing}
                        label={values[FieldName.enableAiPostEditing] ? "Enabled" : "Disabled"}
                        disabled={isAppBundleMode || isReadOnly} // Disabled in app bundle mode or when the client is locked
                      />
                    }
                  />
                </Stack>
              </Box>
            )}

            {/* File Translation Settings */}
            {values[FieldName.enableFileTranslation] && (
              <Box sx={{ border: 1, borderColor: "grey.300", borderRadius: 1, p: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                  File Translation Settings
                </Typography>
                <Stack spacing={3}>
                  <TextInputField
                    name={FieldName.maxFileSizeMB}
                    label="Maximum document file size (MB)"
                    type="number"
                    required={true}
                    disabled={isAppBundleMode || isReadOnly} // Disabled in app bundle mode or when the client is locked
                  />
                  <SelectManyAutocompleteField
                    name={FieldName.allowedFileTypes}
                    label="Allowed document file types"
                    options={allMtFileTypeOptions}
                    disabled={isAppBundleMode || isReadOnly} // Disabled in app bundle mode or when the client is locked
                  />
                  <LabelValue
                    label="File Watermarking"
                    value={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <SwitchField
                          name={FieldName.enableWatermarking}
                          label={values[FieldName.enableWatermarking] ? "Enabled" : "Disabled"}
                          // This field is editable in app bundle mode
                        />
                      </Stack>
                    }
                    info="If enabled, the watermark will be added to the translated files for supported file formats."
                  />
                  {values[FieldName.enableWatermarking] && (
                    <Stack direction="row" spacing={1} alignItems="flex-end">
                      <Box sx={{ flexGrow: 1 }}>
                        <TextInputField
                          name={FieldName.watermarkText}
                          label="Watermark Text"
                          required={true}
                          inputProps={{ maxLength: 30 }}
                          helperText="Maximum 30 characters"
                          // This field is editable in app bundle mode
                        />
                      </Box>
                    </Stack>
                  )}
                </Stack>
              </Box>
            )}

            {/* Multimedia Translation Settings */}
            {values[FieldName.enableMediaTranslation] && (
              <Box sx={{ border: 1, borderColor: "grey.300", borderRadius: 1, p: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                  Multimedia Translation Settings
                </Typography>
                <Stack spacing={3}>
                  <TextInputField
                    name={FieldName.maxAudioFileSizeMB}
                    label="Maximum audio file size (MB)"
                    type="number"
                    required={true}
                    disabled={isAppBundleMode || isReadOnly} // Disabled in app bundle mode or when the client is locked
                  />
                  <TextInputField
                    name={FieldName.maxVideoFileSizeMB}
                    label="Maximum video file size (MB)"
                    type="number"
                    required={true}
                    disabled={isAppBundleMode || isReadOnly} // Disabled in app bundle mode or when the client is locked
                  />
                </Stack>
              </Box>
            )}

            {/* Human Escalation Settings */}
            {/* TODO: Temporarily disable human escalation settings
            {values[FieldName.enableHumanEscalation] && (
              <Box sx={{ border: 1, borderColor: "grey.300", borderRadius: 1, p: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Human Escalation Settings
                  </Typography>
                  {hasOverride("humanEscalationPriceByRating") && (
                    <Chip label="Override" size="small" color="primary" variant="outlined" />
                  )}
                </Stack>
                <Stack spacing={2}>
                  <LabelValue
                    label="Provide Price Estimate"
                    value={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <SwitchField
                          name={FieldName.enableHumanEscalationPricing}
                          label={values[FieldName.enableHumanEscalationPricing] ? "Enabled" : "Disabled"}
                        />
                        {hasOverride("enableHumanEscalationPricing") && (
                          <Chip label="Override" size="small" color="primary" variant="outlined" />
                        )}
                      </Stack>
                    }
                  />
                  {values[FieldName.enableHumanEscalationPricing] && (
                    <>
                      <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                        Pricing per word in {clientBranchCurrency}
                      </Typography>
                      <Stack direction="row" spacing={2}>
                        <Box sx={{ flex: 1 }}>
                          <TextInputField
                            name={FieldName.humanEscalationPriceRating1}
                            label="1-Star"
                            type="number"
                            inputProps={{ step: "0.01", min: "0" }}
                            required={true}
                          />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <TextInputField
                            name={FieldName.humanEscalationPriceRating2}
                            label="2-Star"
                            type="number"
                            inputProps={{ step: "0.01", min: "0" }}
                            required={true}
                          />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <TextInputField
                            name={FieldName.humanEscalationPriceRating3}
                            label="3-Star"
                            type="number"
                            inputProps={{ step: "0.01", min: "0" }}
                            required={true}
                          />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <TextInputField
                            name={FieldName.humanEscalationPriceRating4}
                            label="4-Star"
                            type="number"
                            inputProps={{ step: "0.01", min: "0" }}
                            required={true}
                          />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <TextInputField
                            name={FieldName.humanEscalationPriceRating5}
                            label="5-Star"
                            type="number"
                            inputProps={{ step: "0.01", min: "0" }}
                            required={true}
                          />
                        </Box>
                      </Stack>
                    </>
                  )}
                </Stack>
              </Box>
            )}
            */}

            {/* AI Linguistic Quality Review Settings */}
            {values[FieldName.enableAiLinguisticQualityReview] && (
              <Box sx={{ border: 1, borderColor: "grey.300", borderRadius: 1, p: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                  AI Linguistic Quality Review Settings
                </Typography>
                <Stack spacing={3}>
                  <TextInputField
                    name={FieldName.aiLinguisticQualityReviewSampleSize}
                    label="AI Linguistic Quality Review Sample Size (characters)"
                    type="number"
                    required={true}
                    disabled={isAppBundleMode || isReadOnly} // Disabled in app bundle mode or when the client is locked
                  />
                  <TextInputField
                    name={FieldName.aiLinguisticQualityReviewDocumentSamplePageCount}
                    label="AI Linguistic Quality Review Document Sample Page Count"
                    type="number"
                    required={true}
                    disabled={isAppBundleMode || isReadOnly} // Disabled in app bundle mode or when the client is locked
                  />
                  <LabelValue
                    label="Show AI Linguistic Quality Score Per File"
                    value={
                      <SwitchField
                        name={FieldName.showAiLinguisticQualityScorePerFile}
                        label={values[FieldName.showAiLinguisticQualityScorePerFile] ? "Enabled" : "Disabled"}
                        disabled={isAppBundleMode || isReadOnly} // Disabled in app bundle mode or when the client is locked
                      />
                    }
                  />
                </Stack>
              </Box>
            )}

            {/* AI Toolbox Settings */}
            {values[FieldName.enableAiToolbox] && (
              <Box sx={{ border: 1, borderColor: "grey.300", borderRadius: 1, p: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                  AI Toolbox Settings
                </Typography>
                <Stack spacing={3}>
                  <LabelValue
                    label="Show 'Create Task' Button"
                    value={
                      <SwitchField
                        name={FieldName.showCreateTaskButton}
                        label={values[FieldName.showCreateTaskButton] ? "Shown" : "Hidden"}
                        disabled={isAppBundleMode || isReadOnly} // Disabled in app bundle mode or when the client is locked
                      />
                    }
                  />
                  <LabelValue
                    label="Show 'Improve Text' Action"
                    value={
                      <SwitchField
                        name={FieldName.showPostEditAction}
                        label={values[FieldName.showPostEditAction] ? "Shown" : "Hidden"}
                        disabled={isAppBundleMode || isReadOnly} // Disabled in app bundle mode or when the client is locked
                      />
                    }
                  />
                  <LabelValue
                    label="Show 'Add Watermarks' Action"
                    value={
                      <SwitchField
                        name={FieldName.showWatermarkAction}
                        label={values[FieldName.showWatermarkAction] ? "Shown" : "Hidden"}
                        disabled={isAppBundleMode || isReadOnly} // Disabled in app bundle mode or when the client is locked
                      />
                    }
                  />
                </Stack>
              </Box>
            )}

            <Stack direction="row" spacing={1}>
              <LoadingButton isLoading={isSubmitting} disabled={isReadOnly}>
                Save
              </LoadingButton>
              <LoadingButton
                isLoading={isMutating && !isSubmitting}
                color="secondary"
                onClick={onResetToDefaultClick}
                variant="text"
                disabled={isReadOnly}
              >
                {isAppBundleMode ? "Clear overrides" : "Reset to default"}
              </LoadingButton>
            </Stack>
          </Stack>
        </Form>
      )}
    </Formik>
  );
};

export { MachineTranslationSettings };
